import { FormProvider, getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import { Card, Container, Flex, ScrollArea } from '@radix-ui/themes'
import { type MetaArgs, type MetaFunction } from '@remix-run/node'
import {
	type ShouldRevalidateFunctionArgs,
	NavLink,
	Outlet,
	useLoaderData,
	useOutletContext,
	useFetcher,
	useLocation,
} from '@remix-run/react'
import cn from 'clsx'
import isEqual from 'lodash.isequal'
import uniqWith from 'lodash.uniqwith'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RxExit, RxHeart } from 'react-icons/rx'

import { z } from 'zod'
import { FormDefaults } from '~/components/common/form-defaults.tsx'
import { SubmissionButton } from '~/components/common/submission-button.tsx'
import { HiOutlineMapPin } from "react-icons/hi2";
import { LuPackageOpen } from "react-icons/lu";
import { RxPerson } from "react-icons/rx";
import { type Locale } from '~/models/locale'
import { NEWSLETTER_FORM } from '~/routes/($lang).newsletter/newsletter.component'
import { PRODUCTS_SLUG_FORM_ADD_TO_CART } from '~/routes/($lang).products.$slug/slug.component'
import { type loader } from '~/routes/($lang).profile/profile.server'
import { useProfileNotify } from '~/routes/($lang).profile/use-profile-notify.tsx'
import { PROFILE_ADDRESSES_FORM_REMOVE_ADDRESS } from '~/routes/($lang).profile.addresses/addresses.component'
import {
	PROFILE_WISHLIST_FORM_ADD_TO_WISHLIST,
	PROFILE_WISHLIST_FORM_REMOVE_FROM_WISHLIST,
} from '~/routes/($lang).profile.wishlist/wishlist.component'
import {
	type Context,
	type FormEnum,
	ROOT_INPUTS,
	ROOT_SEARCH_PARAMS,
} from '~/routes/_layout/root.component'
import { type loader as RouteLoader } from '~/routes/_layout/root.server'
import { defaultMetaI18next } from '~/utils/client/default-meta-i18next.ts'
import { schemaDefault } from '~/utils/form-defaults.ts'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	routeProfile = 'route-profile',
}

/* -------------------------------------------------------------------------------------------------
 * Route loader
 * -----------------------------------------------------------------------------------------------*/

enum ROUTE_LOADER {
	root = 'root',
	current = 'routes/($lang).profile',
}

/* -------------------------------------------------------------------------------------------------
 * Logout form
 * -----------------------------------------------------------------------------------------------*/

const FORM_LOGOUT: FormEnum = {
	id: 'profile-logout-form',
	action: '/profile',
	fetcherKey: 'profile-logout',
	subaction: 'profile-logout',
	redirect: '/login',
} as const

const logoutSchema = z.object({
	...schemaDefault,
})

/* -------------------------------------------------------------------------------------------------
 * Resend verification form
 * -----------------------------------------------------------------------------------------------*/

const FORM_RESEND_VERIFICATION_EMAIL_FORM: FormEnum = {
	id: 'profile-resend-verification-email-form',
	action: '/profile',
	fetcherKey: 'profile-resend-verification-email',
	subaction: 'profile-resend-verification-email',
	redirect: '/profile',
} as const

const resendVerificationEmailSchema = z.object({
	...schemaDefault,
})

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Meta
 * -----------------------------------------------------------------------------------------------*/

const meta: MetaFunction<
	typeof loader,
	{
		[ROUTE_LOADER.root]: typeof RouteLoader
	}
> = ({ data, matches }: MetaArgs) => {
	const parentMeta = matches.reduce((a, b) =>
		a.meta.length > b.meta.length ? a : b,
	).meta
	const title = parentMeta.find((meta) => 'title' in meta)
	const description = parentMeta.find(
		(meta) => 'name' in meta && meta.name === 'description',
	)

	const rootData = matches.find((match) => match.id === ROUTE_LOADER.root)!
		.data as { locale: Locale }

	// @ts-ignore
	return uniqWith(
		title?.title.includes('Error')
			? [...parentMeta]
			: data
				? [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{
							title: `${data.customer.firstName} - ${data.meta.title} | ${title?.title}`,
						},
						{
							name: 'description',
							// @ts-ignore
							content: `${data.meta.description} | ${description?.content}`,
						},
						// Structured data -> ld+json
						{
							// @ts-ignore
							'script:ld+json': data.customer,
						},
						// I18next
						...defaultMetaI18next<typeof I18NEXT>({
							I18NEXT,
							locale: rootData.locale,
						}),
					]
				: [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `Error | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `Error | ${description?.content}`,
						},
					],
		(a, b) => isEqual(a, b),
	)
}

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	defaultShouldRevalidate,
	formData,
	nextUrl,
	currentUrl,
}: ShouldRevalidateFunctionArgs) => {
	if (
		formData?.get(ROOT_INPUTS.subaction) ===
			PRODUCTS_SLUG_FORM_ADD_TO_CART.subaction ||
		formData?.get(ROOT_INPUTS.subaction) ===
			PROFILE_WISHLIST_FORM_ADD_TO_WISHLIST.subaction ||
		formData?.get(ROOT_INPUTS.subaction) ===
			PROFILE_WISHLIST_FORM_REMOVE_FROM_WISHLIST.subaction ||
		formData?.get(ROOT_INPUTS.subaction) ===
			PROFILE_ADDRESSES_FORM_REMOVE_ADDRESS.subaction ||
		formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction ||
		nextUrl.searchParams.has(ROOT_SEARCH_PARAMS.page) ||
		(currentUrl.searchParams.has(ROOT_SEARCH_PARAMS.page) &&
			!nextUrl.searchParams.has(ROOT_SEARCH_PARAMS.page))
	) {
		return false
	}

	return defaultShouldRevalidate
}

/* -------------------------------------------------------------------------------------------------
 * Component
 * -----------------------------------------------------------------------------------------------*/

const Component: React.FC = () => {
	const context = useOutletContext<Context>()
	const { locale, setApp } = context
	const { customer } = useLoaderData<typeof loader>()
	const logoutFetcher = useFetcher({ key: FORM_LOGOUT.subaction })
  const logoutFormValues = {
    [ROOT_INPUTS.subaction]: FORM_LOGOUT.subaction,
    [ROOT_INPUTS.noJs]: 'true',
  }
	const [logoutForm, logoutFields] = useForm({
		defaultValue: {
			...logoutFormValues,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: logoutSchema })
		},
		shouldValidate: 'onSubmit',
	})
	const { t } = useTranslation(I18NEXT.routeProfile)
	const location = useLocation()

	useProfileNotify({ setApp, fetcher: logoutFetcher, t, customer })

	return (
		<Container
			px="5"
			mt={{
				initial: '0',
				md: '2',
			}}
			mb="9"
			className="app-Profile"
		>
			<Flex
				direction={{ initial: 'column', md: 'row' }}
				justify="start"
				align="center"
				height="100%"
				gap="5"
				flexGrow="1"
			>
				<NavigationMenu.Root className="app-SidePanel sticky mb-3 mt-4 self-stretch md:mb-0 lg:mt-36">
					<Card size="4">
						<ScrollArea type="auto" scrollbars="horizontal">
							<NavigationMenu.List className="flex h-full w-full justify-center divide-y divide-transparent fill-transparent stroke-gray-10 stroke-[17] text-gray-10 md:flex-col md:divide-gray-6">
								{[
									{ Icon: RxPerson, label: 'profile', to: '' },
									{ Icon: LuPackageOpen, label: 'orders', to: 'orders' },
									{ Icon: HiOutlineMapPin, label: 'addresses', to: 'addresses' },
								].map(({ Icon, label, to }) => (
									<Flex key={to} asChild align="center">
										<NavigationMenu.Item className="flex items-center">
											<NavLink
												prefetch="viewport"
												to={`${to}`}
												end={to === ''}
												className={({ isActive }) =>
													cn(
														'flex items-center gap-x-2 p-4',
														'transition duration-300 ease-in-out',
														'hover:stroke-accent-8 hover:text-accent-8',
														{
															'stroke-accent-8 font-bold text-accent-8':
																isActive,
														},
													)
												}
											>
												<Icon width={35} height={35} size="1.3em" />
												{t(`label.${label}`)}
											</NavLink>
										</NavigationMenu.Item>
									</Flex>
								))}
								<Flex asChild align="center" mt={{ md: 'auto' }}>
									<li>
										<FormProvider context={logoutForm.context}>
											<logoutFetcher.Form
												method="post"
												action={`/${locale}${FORM_LOGOUT.action}${location.search}`}
												{...getFormProps(logoutForm)}
											>
												<FormDefaults
                          form={logoutForm}
                          fields={logoutFields}
                          values={logoutFormValues}
                        />
												<Flex asChild gapX="4" align="center" p="4" ml="2">
													<button
														type="submit"
														className={cn(
															'!font-medium',
															'transition duration-300 ease-in-out',
															'hover:stroke-accent-8 hover:text-accent-8',
															'font-medium shadow-none',
														)}
													>
														<SubmissionButton fetcher={logoutFetcher}>
															<RxExit size="1.3em" />
															{t('label.logout')}
														</SubmissionButton>
													</button>
												</Flex>
											</logoutFetcher.Form>
										</FormProvider>
									</li>
								</Flex>
								<NavigationMenu.Indicator />
							</NavigationMenu.List>
						</ScrollArea>
					</Card>
					<NavigationMenu.Viewport />
				</NavigationMenu.Root>
				<Flex justify="center" width="100%" flexGrow="1" className="mt-4 lg:mt-36">
					<Outlet context={context} />
				</Flex>
			</Flex>
		</Container>
	)
}
Component.displayName = 'Component'

/* -----------------------------------------------------------------------------------------------*/

export {
	Component,
	shouldRevalidate,
	handle,
	meta,
	I18NEXT as PROFILE_I18NEXT,
	ROUTE_LOADER as PROFILE_ROUTE_LOADER,
	FORM_LOGOUT as PROFILE_FORM_LOGOUT,
	FORM_RESEND_VERIFICATION_EMAIL_FORM as PROFILE_FORM_RESEND_VERIFICATION_EMAIL_FORM,
	logoutSchema as profileLogoutSchema,
	resendVerificationEmailSchema as profileResendVerificationEmailSchema,
}
